<template>
  <div class="waiter-page">
    <Header hide-portal-shortcuts />

    <div class="waiter-notifications" aria-live="polite">
      <transition-group name="waiter-toast" tag="div" class="waiter-notifications__list">
        <div
          v-for="note in notifications"
          :key="note.id"
          class="waiter-notifications__item"
          :class="`waiter-notifications__item--${note.type}`"
        >
          <i :class="note.icon" aria-hidden="true"></i>
          <span>{{ note.message }}</span>
          <button type="button" class="waiter-notifications__close" @click="dismissNotification(note.id)">
            <i class="icon_close_alt2" aria-hidden="true"></i>
          </button>
        </div>
      </transition-group>
    </div>

    <section class="waiter-toolbar">
      <label class="waiter-toolbar__search" for="waiter-table-search">
        <i class="icon_search" aria-hidden="true"></i>
        <input
          id="waiter-table-search"
          v-model="search"
          type="search"
          placeholder="Search tables"
          autocomplete="off"
          @input="fetchTables"
        />
      </label>

      <div class="waiter-toolbar__actions">
        <button
          type="button"
          class="waiter-toolbar__btn"
          :class="{ 'waiter-toolbar__btn--active': mergeMode }"
          @click="toggleMergeMode"
        >
          <i class="icon_link_alt" aria-hidden="true"></i>
          <span>{{ mergeMode ? 'Cancel merge' : 'Merge tables' }}</span>
        </button>
        <button type="button" class="waiter-toolbar__btn" @click="refreshAll">
          <i class="icon_refresh" aria-hidden="true"></i>
          <span>Refresh</span>
        </button>
      </div>
    </section>

    <div v-if="mergeMode && selectedTableIds.length" class="waiter-merge-bar">
      <span>{{ selectedTableIds.length }} table(s) selected</span>
      <button
        type="button"
        class="waiter-merge-bar__btn"
        :disabled="selectedTableIds.length < 2 || merging"
        @click="confirmMerge"
      >
        Merge for one customer
      </button>
    </div>

    <div class="waiter-layout">
      <section class="waiter-tables" aria-label="Tables">
        <h2 class="waiter-section-title">Tables</h2>
        <div v-if="loadingTables" class="waiter-grid waiter-grid--loading">
          <div v-for="n in 8" :key="`sk-${n}`" class="waiter-card waiter-card--skeleton"></div>
        </div>
        <div v-else class="waiter-grid">
          <button
            v-for="table in displayTables"
            :key="table.id"
            type="button"
            class="waiter-card"
            :class="tableCardClass(table)"
            @click="handleTableClick(table)"
          >
            <div class="waiter-card__head">
              <strong>{{ tableDisplayName(table) }}</strong>
              <span class="waiter-card__chip" :class="tableStatusClass(table)">
                {{ tableStatusLabel(table) }}
              </span>
            </div>
            <div class="waiter-card__meta">
              <span>{{ table.type }}</span>
              <span v-if="table.capacity">Seats {{ table.capacity }}</span>
            </div>
            <div v-if="table.waiter && table.waiter.name" class="waiter-card__waiter">
              Waiter: {{ table.waiter.name }}
            </div>
            <div v-if="isMergedPrimary(table)" class="waiter-card__merged">
              Merged group
            </div>
            <div v-if="table.is_order && table.order" class="waiter-card__order">
              <span>#{{ table.order.order_number }}</span>
              <span>{{ formatStatus(table.order.status) }}</span>
              <span v-if="table.order.kitchen_status" class="waiter-card__kitchen">
                Kitchen: {{ formatStatus(table.order.kitchen_status) }}
              </span>
            </div>
            <div v-if="isTableMerged(table)" class="waiter-card__merged">
              One bill for all merged tables
            </div>
            <div v-if="isHeldByOtherWaiter(table)" class="waiter-card__blocked">
              {{
                canHandleOtherTables
                  ? "Tap to add items"
                  : `Picked by ${table.waiter.name}`
              }}
            </div>
            <button
              v-if="canCancelTable(table)"
              type="button"
              class="waiter-card__cancel"
              @click.stop="cancelTableOrder(table)"
            >
              {{ table.order.status === 'receiving' ? 'Release table' : 'Cancel' }}
            </button>
          </button>
        </div>
      </section>

      <section class="waiter-orders" aria-label="Live orders">
        <div class="waiter-orders__head">
          <h2 class="waiter-section-title">
            Live orders
            <span class="waiter-live-dot" title="Auto-refreshing"></span>
          </h2>
          <button
            v-if="filteredTableId"
            type="button"
            class="waiter-orders__clear"
            @click="clearTableFilter"
          >
            Show all orders
          </button>
        </div>

        <p v-if="filteredTableLabel" class="waiter-orders__filter">
          Showing orders for <strong>{{ filteredTableLabel }}</strong>
        </p>

        <div v-if="!filteredOrders.length" class="waiter-empty">
          {{
            filteredTableId
              ? "No live order for this table. Use Add items when an order exists."
              : "No active orders"
          }}
        </div>

        <article
          v-for="order in filteredOrders"
          :key="order.id"
          class="waiter-order-card"
          :class="{ 'waiter-order-card--selected': selectedOrder && selectedOrder.id === order.id }"
          @click="openLiveOrder(order)"
        >
          <div class="waiter-order-card__head">
            <div>
              <strong>#{{ order.order_number }}</strong>
              <p v-if="order.table">{{ order.table.name }}</p>
            </div>
            <span class="waiter-order-card__badge" :class="statusBadgeClass(order.status)">
              {{ formatStatus(order.status) }}
            </span>
          </div>

          <div class="waiter-order-card__kitchen" v-if="order.kitchen_status">
            Kitchen: {{ formatStatus(order.kitchen_status) }}
          </div>
          <div v-if="order.waiter && order.waiter.name" class="waiter-order-card__waiter">
            Waiter: {{ order.waiter.name }}
          </div>

          <ul class="waiter-order-card__items">
            <li v-for="(item, idx) in order.items.slice(0, 4)" :key="`${order.id}-${idx}`">
              <span>{{ item.name }}</span>
              <span>x{{ item.qty }}</span>
            </li>
            <li v-if="order.items.length > 4" class="waiter-order-card__more">
              +{{ order.items.length - 4 }} more
            </li>
          </ul>

          <div class="waiter-order-card__actions" @click.stop>
            <button
              v-if="canMarkWaiting(order) && !isOtherWaiterOrder(order)"
              type="button"
              class="waiter-action waiter-action--warn"
              @click="updateOrderStatus(order.id, 'waiting')"
            >
              Ready
            </button>
            <button
              v-if="canMarkServed(order) && !isOtherWaiterOrder(order)"
              type="button"
              class="waiter-action waiter-action--success"
              @click="updateOrderStatus(order.id, 'served')"
            >
              Served
            </button>
            <button
              v-if="!isOtherWaiterOrder(order)"
              type="button"
              class="waiter-action"
              @click="goAddItems(order)"
            >
              Add items
            </button>
            <button
              v-if="canCancelOrder(order)"
              type="button"
              class="waiter-action waiter-action--danger"
              @click="cancelOrder(order)"
            >
              {{ order.status === 'receiving' ? 'Release' : 'Cancel' }}
            </button>
          </div>
        </article>
      </section>
    </div>

    <transition name="waiter-fade">
      <div
        v-if="cancelDialog.visible"
        class="waiter-cancel-modal"
        role="presentation"
        @click.self="closeCancelDialog()"
      >
        <div
          class="waiter-cancel-modal__panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby="waiter-cancel-title"
        >
          <div class="waiter-cancel-modal__icon" aria-hidden="true">
            <i class="icon_error-circle_alt"></i>
          </div>
          <h3 id="waiter-cancel-title" class="waiter-cancel-modal__title">
            {{ cancelDialog.isHeld ? "Release table?" : "Cancel order?" }}
          </h3>
          <p class="waiter-cancel-modal__text">
            <template v-if="cancelDialog.isHeld">
              Release <strong>{{ cancelDialog.tableName }}</strong>? It will be available for other waiters.
            </template>
            <template v-else>
              Cancel order
              <strong v-if="cancelDialog.order && cancelDialog.order.order_number">
                #{{ cancelDialog.order.order_number }}
              </strong>
              for <strong>{{ cancelDialog.tableName }}</strong>? This cannot be undone.
            </template>
          </p>
          <label class="waiter-cancel-modal__field">
            <span>
              Reason for {{ cancelDialog.isHeld ? "release" : "cancellation" }}
              <span class="waiter-cancel-modal__required">*</span>
            </span>
            <textarea
              v-model="cancelDialog.reason"
              rows="3"
              maxlength="500"
              placeholder="e.g. Customer left, wrong table, duplicate order..."
              :disabled="cancelDialog.submitting"
            />
          </label>
          <p v-if="cancelDialog.error" class="waiter-cancel-modal__error" role="alert">
            {{ cancelDialog.error }}
          </p>
          <div class="waiter-cancel-modal__actions">
            <button
              type="button"
              class="waiter-cancel-modal__btn waiter-cancel-modal__btn--ghost"
              :disabled="cancelDialog.submitting"
              @click="closeCancelDialog()"
            >
              Keep order
            </button>
            <button
              type="button"
              class="waiter-cancel-modal__btn waiter-cancel-modal__btn--danger"
              :disabled="cancelDialog.submitting"
              @click="submitCancelOrder"
            >
              {{
                cancelDialog.submitting
                  ? "Please wait…"
                  : cancelDialog.isHeld
                    ? "Release table"
                    : "Cancel order"
              }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from "@/plugins/axios";
import { Header } from "@/components/index.js";
import { mapState } from "vuex";
import { canHandleOtherWaiterTable, isTableLockedToOtherWaiter } from "@/utils/permissions";

export default {
  name: "WaiterDashboard",
  components: { Header },
  data() {
    return {
      search: "",
      loadingTables: false,
      merging: false,
      mergeMode: false,
      selectedTableIds: [],
      primaryTableId: null,
      Tables: [],
      orders: [],
      previousOrderSnapshot: {},
      notifications: [],
      notificationCounter: 0,
      pollingInterval: null,
      selectedOrder: null,
      filteredTableId: null,
      cancelDialog: {
        visible: false,
        order: null,
        tableName: "",
        isHeld: false,
        reason: "",
        error: "",
        submitting: false,
      },
    };
  },
  computed: {
    ...mapState("auth", ["user"]),
    waiterFilterId() {
      if (this.user && this.user.waiter_id) return this.user.waiter_id;
      return null;
    },
    canHandleOtherTables() {
      return canHandleOtherWaiterTable(this.$store.state.auth);
    },
    displayTables() {
      return (this.Tables || []).filter((table) => !table.is_merged_secondary);
    },
    filteredOrders() {
      const list = (this.orders || []).filter(
        (order) => !this.isOtherWaiterOrder(order),
      );
      if (!this.filteredTableId) return list;
      return list.filter((order) =>
        this.orderMatchesTableFilter(order, this.filteredTableId),
      );
    },
    filteredTableLabel() {
      if (!this.filteredTableId) return "";
      const table = this.Tables.find(
        (t) => Number(t.id) === Number(this.filteredTableId),
      );
      return table ? this.tableDisplayName(table) : `Table ${this.filteredTableId}`;
    },
  },
  created() {
    this.refreshAll();
    this.pollingInterval = setInterval(() => this.refreshAll(true), 12000);
  },
  beforeDestroy() {
    if (this.pollingInterval) clearInterval(this.pollingInterval);
  },
  methods: {
    getApiErrorMessage(err, fallback) {
      const data = err && err.response && err.response.data;
      if (typeof data === "string" && data.trim()) {
        if (data.includes("Cannot POST") || data.includes("Cannot GET")) {
          return "Server is missing the latest waiter APIs. Deploy GHMS_Back, then run npm run sync:waiter-portal.";
        }
        if (data.toLowerCase().includes("token not found")) {
          return "Session expired. Please sign in to the waiter app again.";
        }
        return data.trim();
      }
      if (data && data.message) return data.message;
      const status = err && err.response && err.response.status;
      if (status === 401) {
        return "Session expired. Please sign in to the waiter app again.";
      }
      if (status === 404) {
        return "Server is missing the latest waiter APIs. Deploy GHMS_Back, then run npm run sync:waiter-portal.";
      }
      return fallback;
    },
    async refreshAll(silent = false) {
      await Promise.all([this.fetchTables(silent), this.fetchOrders(silent)]);
    },
    async fetchTables(silent = false) {
      if (!silent) this.loadingTables = true;
      try {
        const res = await axios.get(`hms/web/tables?query=${encodeURIComponent(this.search || "")}`);
        this.Tables = res.data || [];
      } catch (err) {
        if (!silent) this.pushNotification("Could not load tables", "error");
      } finally {
        this.loadingTables = false;
      }
    },
    async fetchOrders(silent = false) {
      try {
        const params = this.waiterFilterId ? `?waiter=${this.waiterFilterId}` : "";
        const res = await axios.get(`hms/web/orders/waiter/orders${params}`);
        const nextOrders = (res.data && res.data.data) || [];
        this.detectOrderChanges(nextOrders);
        this.orders = nextOrders;
      } catch (err) {
        if (!silent) this.pushNotification("Could not load orders", "error");
      }
    },
    detectOrderChanges(nextOrders) {
      nextOrders.forEach((order) => {
        const prev = this.previousOrderSnapshot[order.id];
        if (!prev) return;

        if (prev.kitchen_status !== order.kitchen_status) {
          this.pushNotification(
            `Order #${order.order_number} kitchen status: ${this.formatStatus(order.kitchen_status)}`,
            "info",
            "icon-food_icon_cloche",
          );
        }
        if (prev.status !== order.status) {
          this.pushNotification(
            `Order #${order.order_number} is now ${this.formatStatus(order.status)}`,
            "success",
            "icon_tag",
          );
        }
      });

      const snapshot = {};
      nextOrders.forEach((order) => {
        snapshot[order.id] = {
          status: order.status,
          kitchen_status: order.kitchen_status,
        };
      });
      this.previousOrderSnapshot = snapshot;
    },
    pushNotification(message, type = "info", icon = "icon_info_alt") {
      const id = ++this.notificationCounter;
      this.notifications.unshift({ id, message, type, icon });
      setTimeout(() => this.dismissNotification(id), 8000);
    },
    dismissNotification(id) {
      this.notifications = this.notifications.filter((n) => n.id !== id);
    },
    toggleMergeMode() {
      this.mergeMode = !this.mergeMode;
      this.selectedTableIds = [];
      this.primaryTableId = null;
    },
    tableCardClass(table) {
      return {
        "waiter-card--busy": this.isTableOccupied(table),
        "waiter-card--held": table.occupancy_status === "held" && this.isOwnedByMe(table),
        "waiter-card--other": this.isHeldByOtherWaiter(table) && !this.canHandleOtherTables,
        "waiter-card--shared": this.isHeldByOtherWaiter(table) && this.canHandleOtherTables,
        "waiter-card--selected": this.selectedTableIds.includes(table.id),
        "waiter-card--filtered": Number(this.filteredTableId) === Number(table.id),
        "waiter-card--available": !this.isTableOccupied(table),
        "waiter-card--disabled": this.isHeldByOtherWaiter(table) && !this.canHandleOtherTables,
      };
    },
    tableStatusClass(table) {
      if (this.isHeldByOtherWaiter(table)) return "waiter-card__chip--other";
      if (this.isTableOccupied(table)) return "waiter-card__chip--busy";
      return "waiter-card__chip--free";
    },
    tableStatusLabel(table) {
      if (this.isHeldByOtherWaiter(table) && table.waiter && table.waiter.name) {
        return `Picked by ${table.waiter.name}`;
      }
      if (table.occupancy_label) return table.occupancy_label;
      if (this.isHeldByOtherWaiter(table)) return "Other waiter";
      if (this.isTableOccupied(table)) return "In service";
      return "Available";
    },
    isTableOccupied(table) {
      return !!(table && table.is_order);
    },
    isOwnedByMe(table) {
      if (!this.waiterFilterId || !table) return false;
      const waiterId = table.waiter_id || (table.waiter && table.waiter.id) || (table.order && table.order.waiter && table.order.waiter.id);
      return Number(waiterId) === Number(this.waiterFilterId);
    },
    isHeldByOtherWaiter(table) {
      if (!table || !this.isTableOccupied(table) || this.isOwnedByMe(table)) return false;
      const waiterId =
        table.waiter_id
        || (table.waiter && table.waiter.id)
        || (table.order && table.order.waiter_id)
        || (table.order && table.order.waiter && table.order.waiter.id);
      return !!(waiterId || (table.waiter && table.waiter.name));
    },
    isOtherWaiterOrder(order) {
      return isTableLockedToOtherWaiter(this.$store.state.auth, order);
    },
    isTableMerged(table) {
      return (
        table.is_order &&
        table.order &&
        Array.isArray(table.order.merged_table_ids) &&
        table.order.merged_table_ids.length > 0
      );
    },
    isMergedPrimary(table) {
      return !!(
        table
        && table.merged_table_names
        && table.merged_table_names.length
      );
    },
    tableDisplayName(table) {
      if (!table) return "";
      if (this.isMergedPrimary(table)) {
        return [table.name, ...table.merged_table_names].join(" + ");
      }
      return table.name;
    },
    orderMatchesTableFilter(order, tableId) {
      const id = Number(tableId);
      if (order.table && Number(order.table.id) === id) return true;
      const mergedIds = Array.isArray(order.merged_table_ids)
        ? order.merged_table_ids.map(Number)
        : [];
      return mergedIds.includes(id);
    },
    clearTableFilter() {
      this.filteredTableId = null;
      this.selectedOrder = null;
    },
    filterByTable(table) {
      this.filteredTableId = table.id;
      if (table.is_order && table.order && table.order.id) {
        this.selectOrderFromTable(table);
      } else {
        this.selectedOrder = null;
      }
    },
    handleTableClick(table) {
      if (this.isHeldByOtherWaiter(table)) {
        if (!this.canHandleOtherTables) {
          this.pushNotification(
            `${table.name} is already picked by ${table.waiter.name}`,
            "error",
          );
          return;
        }
        if (this.mergeMode) {
          this.pushNotification(
            `${table.name} stays with ${table.waiter.name} and cannot be merged`,
            "error",
          );
          return;
        }
        if (table.is_order && table.order && table.order.id) {
          this.openTableOrder(table);
        }
        return;
      }

      if (this.mergeMode) {
        this.toggleTableSelection(table);
        return;
      }

      if (table.is_order && table.order && table.order.id) {
        this.openTableOrder(table);
        return;
      }

      this.claimAndFilterTable(table);
    },
    toggleTableSelection(table) {
      if (this.isHeldByOtherWaiter(table)) {
        this.pushNotification(
          `${table.name} is already picked by another waiter`,
          "error",
        );
        return;
      }
      const idx = this.selectedTableIds.indexOf(table.id);
      if (idx === -1) {
        this.selectedTableIds.push(table.id);
        if (!this.primaryTableId) this.primaryTableId = table.id;
      } else {
        this.selectedTableIds.splice(idx, 1);
        if (this.primaryTableId === table.id) {
          this.primaryTableId = this.selectedTableIds[0] || null;
        }
      }
    },
    async confirmMerge() {
      if (this.selectedTableIds.length < 2 || !this.primaryTableId) return;

      this.merging = true;
      const primaryId = this.primaryTableId;
      try {
        await axios.post("hms/web/orders/merge-tables", {
          primary_table_id: primaryId,
          table_ids: this.selectedTableIds,
          waiter_id: this.waiterFilterId,
        });
        this.pushNotification("Tables merged for one customer", "success", "icon_link_alt");
        this.mergeMode = false;
        this.selectedTableIds = [];
        this.primaryTableId = null;
        await this.refreshAll(true);
        const primaryTable = this.Tables.find((t) => t.id === primaryId);
        if (primaryTable) {
          this.openTableOrder(primaryTable);
        }
      } catch (err) {
        const message = this.getApiErrorMessage(err, "Could not merge tables");
        this.pushNotification(message, "error");
      } finally {
        this.merging = false;
      }
    },
    selectOrderFromTable(table) {
      const match = this.orders.find((o) => o.id === table.order.id);
      this.selectedOrder = match || {
        id: table.order.id,
        order_number: table.order.order_number,
        status: table.order.status,
        kitchen_status: table.order.kitchen_status,
        table: { id: table.id, name: table.name },
        items: table.order.items || [],
      };
    },
    selectOrder(order) {
      this.selectedOrder = order;
      if (order.table && order.table.id) {
        this.filteredTableId = order.table.id;
      }
    },
    async claimAndFilterTable(table) {
      const tableId = typeof table === "object" ? table.id : table;
      const tableRef = typeof table === "object" ? table : this.Tables.find((t) => t.id === tableId);
      try {
        const assigned = await axios.post("hms/web/tables/assign", {
          table_id: tableId,
          waiter_id: this.waiterFilterId,
        });
        const assignedWaiterId = assigned.data && assigned.data.waiter_id;
        if (assignedWaiterId) {
          this.$store.commit("auth/SET_USER", {
            ...(this.user || {}),
            waiter_id: assignedWaiterId,
          });
        }
        await this.refreshAll(true);
        const refreshed =
          this.Tables.find((t) => t.id === tableId) ||
          tableRef || { id: tableId, name: `Table ${tableId}` };
        this.openTableOrder(refreshed);
      } catch (err) {
        const message = this.getApiErrorMessage(err, "Could not reserve table");
        this.pushNotification(message, "error");
      }
    },
    openTableOrder(table) {
      if (!table || !table.id) return;
      const order = table.order || {};
      this.goAddItems({
        table: { id: table.id, name: table.name },
        guest: order.guest,
      });
    },
    openLiveOrder(order) {
      if (this.isOtherWaiterOrder(order)) {
        const name = (order.waiter && order.waiter.name) || "another waiter";
        this.pushNotification(`This table is picked by ${name}`, "error");
        return;
      }
      this.goAddItems(order);
    },
    goAddItems(order) {
      if (this.isOtherWaiterOrder(order)) {
        const name = (order.waiter && order.waiter.name) || "another waiter";
        this.pushNotification(`This table is picked by ${name}`, "error");
        return;
      }
      const tableId = order && order.table ? order.table.id : null;
      if (!tableId) return;
      if (order.guest) {
        this.$store.commit("orders/AddGuestDetails", order.guest);
      }
      this.$router.push(`/order/table/${tableId}`);
    },
    async updateOrderStatus(orderId, status) {
      try {
        await axios.patch(`hms/web/orders/${orderId}/waiter-status`, { status });
        this.pushNotification(`Order marked as ${this.formatStatus(status)}`, "success");
        await this.fetchOrders(true);
        await this.fetchTables(true);
      } catch (err) {
        const message =
          (err.response && err.response.data && err.response.data.message) ||
          "Status update failed";
        this.pushNotification(message, "error");
      }
    },
    canMarkWaiting(order) {
      return (
        order.kitchen_status === "finished" &&
        !["waiting", "served", "cancelled", "paid", "completed"].includes(order.status)
      );
    },
    canMarkServed(order) {
      return ["waiting", "cooked"].includes(order.status);
    },
    canCancelOrder(order) {
      if (!order || !order.id) return false;
      const waiterId = order.waiter_id || (order.waiter && order.waiter.id);
      if (
        this.waiterFilterId &&
        waiterId &&
        Number(waiterId) !== Number(this.waiterFilterId)
      ) {
        return false;
      }
      return ![
        "cancelled",
        "paid",
        "completed",
        "partially_paid",
        "cooking",
        "cooked",
      ].includes(order.status);
    },
    canCancelTable(table) {
      if (!this.isOwnedByMe(table) || !table.order) return false;
      return this.canCancelOrder({
        id: table.order.id,
        status: table.order.status,
        waiter_id: table.waiter_id || (table.waiter && table.waiter.id),
      });
    },
    openCancelDialog(order, tableName) {
      const label = tableName || (order.table && order.table.name) || "this table";
      this.cancelDialog = {
        visible: true,
        order,
        tableName: label,
        isHeld: order.status === "receiving",
        reason: "",
        error: "",
        submitting: false,
      };
    },
    closeCancelDialog(force = false) {
      if (this.cancelDialog.submitting && force !== true) return;
      this.cancelDialog.visible = false;
      this.cancelDialog.order = null;
      this.cancelDialog.reason = "";
      this.cancelDialog.error = "";
      this.cancelDialog.submitting = false;
    },
    async submitCancelOrder() {
      const reason = (this.cancelDialog.reason || "").trim();
      if (reason.length < 3) {
        this.cancelDialog.error = "Please enter a reason (at least 3 characters).";
        return;
      }

      const order = this.cancelDialog.order;
      if (!order || !order.id) return;

      const isHeld = this.cancelDialog.isHeld;
      this.cancelDialog.error = "";
      this.cancelDialog.submitting = true;

      try {
        await axios.patch(`hms/web/orders/${order.id}/waiter-cancel`, {
          waiter_id: this.waiterFilterId,
          note: reason,
        });
        this.pushNotification(
          isHeld ? "Table released" : "Order cancelled",
          "success",
        );
        if (this.selectedOrder && this.selectedOrder.id === order.id) {
          this.selectedOrder = null;
        }
        this.closeCancelDialog(true);
        await this.refreshAll(true);
      } catch (err) {
        this.cancelDialog.error = this.getApiErrorMessage(err, "Could not cancel");
        this.cancelDialog.submitting = false;
      }
    },
    cancelOrder(order, tableName) {
      this.openCancelDialog(order, tableName);
    },
    cancelTableOrder(table) {
      if (!table.order) return;
      this.openCancelDialog(
        {
          id: table.order.id,
          status: table.order.status,
          order_number: table.order.order_number,
          waiter_id: table.waiter_id || (table.waiter && table.waiter.id),
          table: { name: table.name },
        },
        table.name,
      );
    },
    statusBadgeClass(status) {
      if (status === "served") return "waiter-order-card__badge--served";
      if (status === "waiting") return "waiter-order-card__badge--waiting";
      return "waiter-order-card__badge--default";
    },
    formatStatus(value) {
      if (!value) return "-";
      return String(value).replace(/_/g, " ");
    },
  },
};
</script>

<style scoped>
.waiter-page {
  min-height: 100vh;
  padding-top: 60px;
  background: #f8fafc;
  box-sizing: border-box;
}

.waiter-notifications {
  position: fixed;
  top: 72px;
  right: 16px;
  z-index: 20;
  width: min(360px, calc(100vw - 32px));
}

.waiter-notifications__list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.waiter-notifications__item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 10px;
  background: #fff;
  border: 1px solid #e2e8f0;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
  font-size: 13px;
  color: #1e293b;
}

.waiter-notifications__item--success {
  border-color: #86efac;
  background: #f0fdf4;
}

.waiter-notifications__item--error {
  border-color: #fca5a5;
  background: #fef2f2;
}

.waiter-notifications__item--info {
  border-color: var(--brand-primary-border, rgba(81, 45, 130, 0.25));
  background: var(--brand-primary-muted, rgba(81, 45, 130, 0.06));
}

.waiter-notifications__close {
  margin-left: auto;
  border: none;
  background: transparent;
  color: #64748b;
  cursor: pointer;
}

.waiter-toolbar {
  position: sticky;
  top: 60px;
  z-index: 12;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #f1f5f9;
  border-bottom: 1px solid #e2e8f0;
}

.waiter-toolbar__search {
  position: relative;
  flex: 1 1 240px;
  max-width: 420px;
}

.waiter-toolbar__search i {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--brand-primary);
}

.waiter-toolbar__search input {
  width: 100%;
  height: 40px;
  padding: 0 12px 0 38px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
}

.waiter-toolbar__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.waiter-toolbar__btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 40px;
  padding: 0 14px;
  border: 1px solid var(--brand-primary-border, rgba(81, 45, 130, 0.22));
  border-radius: 8px;
  background: #fff;
  color: var(--brand-primary-dark, #3f2366);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.waiter-toolbar__btn--active {
  background: var(--brand-primary-muted, rgba(81, 45, 130, 0.08));
  border-color: var(--brand-primary);
}

.waiter-merge-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 16px;
  background: #fff7ed;
  border-bottom: 1px solid #fed7aa;
  color: #9a3412;
  font-size: 13px;
}

.waiter-merge-bar__btn {
  border: none;
  border-radius: 8px;
  padding: 8px 14px;
  background: #ea580c;
  color: #fff;
  font-weight: 700;
  cursor: pointer;
}

.waiter-merge-bar__btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.waiter-layout {
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(280px, 0.8fr);
  gap: 16px;
  padding: 16px;
}

.waiter-section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 12px;
  font-size: 16px;
  font-weight: 800;
  color: #1e293b;
}

.waiter-live-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22c55e;
  animation: waiter-pulse 1.4s ease-out infinite;
}

@keyframes waiter-pulse {
  0% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.6); }
  70% { box-shadow: 0 0 0 8px rgba(34, 197, 94, 0); }
  100% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0); }
}

.waiter-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 12px;
}

.waiter-card {
  text-align: left;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #fff;
  padding: 12px;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s, transform 0.12s;
}

.waiter-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.08);
}

.waiter-card--busy {
  border-top: 3px solid #f59e0b;
}

.waiter-card--available {
  border-top: 3px solid #22c55e;
}

.waiter-card--other,
.waiter-card--disabled {
  border-top: 3px solid #ef4444;
  opacity: 0.88;
  cursor: not-allowed;
}

.waiter-card--shared {
  border-top: 3px solid #0ea5e9;
  cursor: pointer;
}

.waiter-card--held {
  border-top: 3px solid #3b82f6;
}

.waiter-card__chip--other {
  background: #fee2e2;
  color: #b91c1c;
}

.waiter-card__waiter {
  margin-top: 6px;
  font-size: 11px;
  font-weight: 600;
  color: #475569;
}

.waiter-card__blocked {
  margin-top: 6px;
  font-size: 10px;
  font-weight: 700;
  color: #b91c1c;
}

.waiter-card__cancel {
  margin-top: 8px;
  width: 100%;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 6px 8px;
  background: #fff;
  color: #b91c1c;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
}

.waiter-card__cancel:hover {
  background: #fef2f2;
}

.waiter-card--selected,
.waiter-card--filtered {
  border-color: var(--brand-primary);
  box-shadow: 0 0 0 2px var(--brand-primary-fade-20, rgba(81, 45, 130, 0.15));
}

.waiter-card--filtered {
  border-width: 2px;
}

.waiter-card--skeleton {
  min-height: 110px;
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: waiter-shimmer 1.2s infinite;
}

@keyframes waiter-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.waiter-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
}

.waiter-card__chip {
  font-size: 10px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 999px;
}

.waiter-card__chip--free {
  background: #dcfce7;
  color: #166534;
}

.waiter-card__chip--busy {
  background: #fef3c7;
  color: #92400e;
}

.waiter-card__meta,
.waiter-card__order {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 11px;
  color: #64748b;
}

.waiter-card__kitchen {
  color: var(--brand-primary-dark, #3f2366);
  font-weight: 600;
}

.waiter-card__merged {
  margin-top: 6px;
  font-size: 10px;
  font-weight: 700;
  color: #ea580c;
}

.waiter-orders {
  min-width: 0;
}

.waiter-orders__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}

.waiter-orders__head .waiter-section-title {
  margin-bottom: 0;
}

.waiter-orders__clear {
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 6px 10px;
  background: #fff;
  color: #475569;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.waiter-orders__clear:hover {
  background: #f8fafc;
}

.waiter-orders__filter {
  margin: 0 0 12px;
  padding: 8px 10px;
  border-radius: 8px;
  background: var(--brand-primary-muted, #f3eef9);
  border: 1px solid var(--brand-primary-border, #e2d8f0);
  font-size: 12px;
  color: #475569;
}

.waiter-empty {
  padding: 24px;
  text-align: center;
  color: #64748b;
  background: #fff;
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
}

.waiter-order-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px;
  margin-bottom: 12px;
  cursor: pointer;
}

.waiter-order-card--selected {
  border-color: var(--brand-primary);
  box-shadow: 0 0 0 2px var(--brand-primary-fade-20, rgba(81, 45, 130, 0.12));
}

.waiter-order-card__head {
  display: flex;
  justify-content: space-between;
  gap: 10px;
}

.waiter-order-card__head p {
  margin: 2px 0 0;
  font-size: 12px;
  color: #64748b;
}

.waiter-order-card__badge {
  align-self: flex-start;
  font-size: 10px;
  font-weight: 700;
  padding: 4px 8px;
  border-radius: 999px;
  background: #f1f5f9;
  color: #475569;
  white-space: nowrap;
}

.waiter-order-card__badge--waiting {
  background: #fef3c7;
  color: #92400e;
}

.waiter-order-card__badge--served {
  background: #dcfce7;
  color: #166534;
}

.waiter-order-card__kitchen {
  margin-top: 8px;
  font-size: 11px;
  color: var(--brand-primary-dark, #3f2366);
  font-weight: 600;
}

.waiter-order-card__items {
  list-style: none;
  margin: 10px 0;
  padding: 0;
}

.waiter-order-card__items li {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 12px;
  padding: 4px 0;
  border-bottom: 1px solid #f1f5f9;
}

.waiter-order-card__more {
  color: #64748b;
  font-style: italic;
}

.waiter-order-card__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.waiter-action {
  flex: 1 1 auto;
  min-width: 80px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  padding: 8px 10px;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
}

.waiter-action--warn {
  background: #fef3c7;
  border-color: #fcd34d;
  color: #92400e;
}

.waiter-action--success {
  background: #dcfce7;
  border-color: #86efac;
  color: #166534;
}

.waiter-action--danger {
  background: #fef2f2;
  border-color: #fecaca;
  color: #b91c1c;
}

.waiter-order-card__waiter {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 8px;
}

.waiter-toast-enter-active,
.waiter-toast-leave-active {
  transition: all 0.25s ease;
}

.waiter-toast-enter,
.waiter-toast-leave-to {
  opacity: 0;
  transform: translateX(12px);
}

@media (max-width: 960px) {
  .waiter-layout {
    grid-template-columns: 1fr;
  }

  .waiter-notifications {
    left: 16px;
    right: 16px;
    width: auto;
  }
}

@media (max-width: 640px) {
  .waiter-grid {
    grid-template-columns: 1fr 1fr;
  }

  .waiter-toolbar__btn span {
    display: none;
  }
}

.waiter-fade-enter-active,
.waiter-fade-leave-active {
  transition: opacity 0.2s ease;
}

.waiter-fade-enter,
.waiter-fade-leave-to {
  opacity: 0;
}

.waiter-cancel-modal {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(2px);
}

.waiter-cancel-modal__panel {
  width: 100%;
  max-width: 440px;
  padding: 24px;
  background: #fff;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 24px 48px rgba(15, 23, 42, 0.18);
}

.waiter-cancel-modal__icon {
  width: 44px;
  height: 44px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: #fef2f2;
  color: #dc2626;
  font-size: 20px;
}

.waiter-cancel-modal__title {
  margin: 0 0 8px;
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
}

.waiter-cancel-modal__text {
  margin: 0 0 16px;
  font-size: 14px;
  line-height: 1.5;
  color: #475569;
}

.waiter-cancel-modal__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 12px;
  text-align: left;
}

.waiter-cancel-modal__field span {
  font-size: 12px;
  font-weight: 700;
  color: #334155;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.waiter-cancel-modal__required {
  color: #dc2626;
}

.waiter-cancel-modal__field textarea {
  width: 100%;
  min-height: 88px;
  padding: 10px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  font-size: 14px;
  font-family: inherit;
  color: #0f172a;
  resize: vertical;
  box-sizing: border-box;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.waiter-cancel-modal__field textarea:focus {
  border-color: #dc2626;
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.12);
}

.waiter-cancel-modal__error {
  margin: 0 0 12px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #b91c1c;
  font-size: 13px;
}

.waiter-cancel-modal__actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

.waiter-cancel-modal__btn {
  min-width: 110px;
  height: 42px;
  padding: 0 16px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}

.waiter-cancel-modal__btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.waiter-cancel-modal__btn--ghost {
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #475569;
}

.waiter-cancel-modal__btn--ghost:hover:not(:disabled) {
  background: #f8fafc;
}

.waiter-cancel-modal__btn--danger {
  border: 1px solid #dc2626;
  background: #dc2626;
  color: #fff;
}

.waiter-cancel-modal__btn--danger:hover:not(:disabled) {
  background: #b91c1c;
  border-color: #b91c1c;
}
</style>
